/**
 * <mf-personalizer> — live preview + validation for the music frame personalizer block.
 * Inputs are tied to the product form through the `form` attribute, so Horizon's FormData
 * submission sends them (files included) as line item properties.
 */
import { StandardEvents } from '@shopify/events';

const MAX_BYTES = 20 * 1024 * 1024;
const PREVIEWABLE = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

class MfPersonalizer extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {string[]} */
  #urls = [];

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.cover = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="cover"]'));
    this.caption = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="caption"]'));
    this.discsWrap = /** @type {HTMLElement | null} */ (this.querySelector('[data-discs]'));
    this.drop = /** @type {HTMLElement | null} */ (this.querySelector('[data-drop]'));
    this.dropText = /** @type {HTMLElement | null} */ (this.querySelector('[data-drop-text]'));
    this.errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    this.preview = document.getElementById(this.dataset.previewId || '');

    this.cover?.addEventListener('change', () => this.#onCover(), { signal });
    this.caption?.addEventListener('input', () => this.#onCaption(), { signal });

    for (const radio of this.querySelectorAll('[data-role="disc-mode"]')) {
      radio.addEventListener('change', () => this.#onDiscMode(), { signal });
    }
    for (const input of this.querySelectorAll('[data-role="disc"]')) {
      input.addEventListener('change', (event) => this.#onDisc(/** @type {HTMLInputElement} */ (event.currentTarget)), {
        signal,
      });
    }

    if (this.drop && this.cover) this.#bindDrop(this.drop, this.cover, signal);

    // Validate before Horizon's product form handles the submit (capture phase on document).
    document.addEventListener('submit', (event) => this.#onSubmit(event), { capture: true, signal });

    // The required cover input is visually hidden, so the browser's own bubble is easy to miss:
    // replace it with the translated message and bring the upload zone into view.
    this.cover?.addEventListener(
      'invalid',
      (event) => {
        event.preventDefault();
        this.#showError(this.dataset.msgRequired || 'Please upload a photo');
        this.drop?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    // Object URLs are kept: the slot may re-attach this element with its previews when the
    // shopper switches back to the personalized variant.
    this.#abort?.abort();
  }

  /** @param {File} file */
  #check(file) {
    if (file.size > MAX_BYTES) {
      this.#showError(this.dataset.msgSize || 'File is too large');
      return false;
    }
    this.#hideError();
    return true;
  }

  /** @param {File} file */
  #url(file) {
    if (!PREVIEWABLE.includes(file.type)) return null;
    const url = URL.createObjectURL(file);
    this.#urls.push(url);
    return url;
  }

  #onCover() {
    const file = this.cover?.files?.[0];
    if (!file || !this.cover) return;
    if (!this.#check(file)) {
      this.cover.value = '';
      return;
    }
    const url = this.#url(file);
    this.drop?.classList.add('is-filled');
    if (this.dropText) this.dropText.textContent = `${file.name} · ${this.dataset.labelChange || 'Change'}`;
    if (url) {
      this.#setPreviewImage('art', url);
      if (!this.#eachDisc()) this.#setPreviewImage('label', url);
    }
  }

  #onCaption() {
    const title = this.preview?.querySelector('[data-mf-preview="title"]');
    if (!title || !this.caption) return;
    if (!this.defaultTitle) this.defaultTitle = title.textContent || '';
    title.textContent = this.caption.value.trim() || this.defaultTitle;
  }

  #eachDisc() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="disc-mode"]:checked'));
    const radios = this.querySelectorAll('[data-role="disc-mode"]');
    return Boolean(checked && radios.length > 1 && checked === radios[1]);
  }

  #onDiscMode() {
    const each = this.#eachDisc();
    if (this.discsWrap) this.discsWrap.hidden = !each;
    for (const input of this.querySelectorAll('[data-role="disc"]')) {
      /** @type {HTMLInputElement} */ (input).disabled = !each;
    }
    const coverFile = this.cover?.files?.[0];
    if (!each && coverFile) {
      const url = this.#url(coverFile);
      if (url) this.#setPreviewImage('label', url);
    }
  }

  /** @param {HTMLInputElement} input */
  #onDisc(input) {
    const file = input.files?.[0];
    if (!file) return;
    if (!this.#check(file)) {
      input.value = '';
      return;
    }
    const url = this.#url(file);
    const slot = input.closest('.mf-pz__disc');
    const img = /** @type {HTMLElement | null} */ (slot?.querySelector('[data-disc-preview]'));
    slot?.classList.add('is-filled');
    if (img && url) img.style.backgroundImage = `url("${url}")`;
    if (url && input.dataset.index === '1') this.#setPreviewImage('label', url);
  }

  /**
   * @param {'art' | 'label'} slot
   * @param {string} url
   */
  #setPreviewImage(slot, url) {
    const target = this.preview?.querySelector(`[data-mf-preview="${slot}"]`);
    if (!target) return;
    let img = /** @type {HTMLImageElement | null} */ (target.querySelector(':scope > img'));
    if (!img) {
      img = document.createElement('img');
      img.alt = '';
      target.prepend(img);
    }
    img.src = url;
  }

  /** @param {HTMLElement} zone @param {HTMLInputElement} input @param {AbortSignal} signal */
  #bindDrop(zone, input, signal) {
    const stop = (/** @type {Event} */ event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    for (const type of ['dragenter', 'dragover']) {
      zone.addEventListener(type, (event) => {
        stop(event);
        zone.classList.add('is-drag');
      }, { signal });
    }
    for (const type of ['dragleave', 'drop']) {
      zone.addEventListener(type, (event) => {
        stop(event);
        zone.classList.remove('is-drag');
      }, { signal });
    }
    zone.addEventListener('drop', (event) => {
      const files = /** @type {DragEvent} */ (event).dataTransfer?.files;
      if (!files?.length) return;
      const transfer = new DataTransfer();
      transfer.items.add(files[0]);
      input.files = transfer.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal });
  }

  /** @param {Event} event */
  #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== this.dataset.formId) return;
    if (!this.isConnected || !this.cover) return;
    if (this.cover.files?.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError(this.dataset.msgRequired || 'Please upload a photo');
    this.drop?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.drop?.focus?.();
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (!this.cover?.files?.length) this.drop?.classList.add('is-invalid');
  }

  #hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
    this.drop?.classList.remove('is-invalid');
  }
}

if (!customElements.get('mf-personalizer')) customElements.define('mf-personalizer', MfPersonalizer);

/**
 * Horizon only morphs the variant picker on a variant change, so the personalizer slot keeps the
 * markup of the first variant. Swap it to the mode rendered for the new variant (personalizer,
 * song list or nothing). Detached nodes are cached per mode, so a photo or song list the shopper
 * already entered comes back when they return to that mode, and inputs that are not in the
 * document are neither validated nor submitted.
 */
/** @type {WeakMap<HTMLElement, Record<string, Node[]>>} */
const slotCache = new WeakMap();

/** @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event */
async function onProductSelect(event) {
  const target = event.target;
  if (!(target instanceof Element) || target.closest('product-card') || !event.promise) return;
  const scope = target.closest('.shopify-section, dialog');
  if (!scope) return;

  let html;
  try {
    html = (await event.promise)?.detail?.html;
  } catch {
    return; // Superseded or failed request: the next selection brings its own markup.
  }
  if (!html) return;

  for (const slot of scope.querySelectorAll('[data-mf-pz-slot]')) {
    if (!(slot instanceof HTMLElement)) continue;
    const fresh = html.querySelector(`[data-mf-pz-slot="${CSS.escape(slot.dataset.mfPzSlot || '')}"]`);
    if (!(fresh instanceof HTMLElement)) continue;

    const current = slot.dataset.mode || 'none';
    const next = fresh.dataset.mode || 'none';
    if (current === next) continue;

    const cache = slotCache.get(slot) || {};
    cache[current] = [...slot.childNodes];
    slot.replaceChildren(...(cache[next] || [...document.importNode(fresh, true).childNodes]));
    slot.dataset.mode = next;
    slot.hidden = next === 'none';
    slotCache.set(slot, cache);
  }

  // Prices rendered by custom blocks (e.g. the bundle tiers) follow the newly selected variant.
  for (const el of scope.querySelectorAll('[data-mf-live]')) {
    if (!(el instanceof HTMLElement)) continue;
    const fresh = html.querySelector(`[data-mf-live="${CSS.escape(el.dataset.mfLive || '')}"]`);
    if (fresh && fresh.textContent !== el.textContent) el.textContent = fresh.textContent;
  }
}

document.addEventListener(StandardEvents.productSelect, onProductSelect, true);
